import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import moment from 'moment-timezone';
import { CHANNEL_LABELS } from './channelCopy';
import ChannelChips from './ChannelChips';
import ProductChips from './ProductChips';
import { scopeFromTours } from './tagScope';
import { detectExpiry, quickStopDates } from './expiryHints';

export { CHANNEL_LABELS };

/*
 * The editable answer: text, which products it applies to, whether to also use it in the
 * other channel (shared answer), and under "More options" an expiry date. Shared by every
 * card kind, and later by the inline "train the bot with your reply" bar in chat. Fully
 * controlled: `draft` holds { answer, all_tours, tours, collections, access_tags, excluded_tours,
 * channels, valid_until, valid_until_reason } -- the scope keys as in tagScope.js; the reason is
 * the phrase a suggested stop date came from.
 *
 * The stop date is easy to forget, so when the answer mentions a date or sounds seasonal
 * the editor asks right under the text ("This answer mentions “until October 31”…").
 * `channel` is the inbox it's edited from; `canShare` = the user can also write to the other.
 * `lockChannel` keeps the inbox's own channel on (training); off when editing a trained
 * answer, so it can be moved to the other channel. `suggestedTours` are the product pills
 * offered (the products guests asked about); "+ Other product" searches `productOptions`.
 */
export default function AnswerEditor({
  question, draft, onChange, productOptions, suggestedTours = [], channel, canShare, lockChannel = true, disabled, api, autoFocus,
}) {
  const [showMore, setShowMore] = useState(!!draft.valid_until);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const set = (patch) => onChange({ ...draft, ...patch });

  // Stop-date suggestion from the answer text; "No thanks" silences that phrase only.
  const [dismissed, setDismissed] = useState(() => new Set());
  const dateFieldRef = useRef(null);
  const expiry = useMemo(() => detectExpiry(draft.answer), [draft.answer]);
  const suggestExpiry = expiry && !draft.valid_until && !dismissed.has(expiry.phrase) && !disabled;
  const acceptExpiry = () => {
    set({ valid_until: expiry.date, valid_until_reason: expiry.phrase });
    setShowMore(true);
  };
  const pickDate = () => {
    setShowMore(true);
    setTimeout(() => dateFieldRef.current?.focus(), 250);
  };
  const dismissExpiry = () => setDismissed((d) => new Set(d).add(expiry.phrase));

  const tryIt = async () => {
    setPreviewing(true);
    try {
      setPreview(await api.preview({ question, answer: draft.answer }));
    } catch {
      setPreview({ error: true });
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <Stack spacing={2}>
      <TextField
        label="Answer the bot should give"
        value={draft.answer}
        onChange={(e) => { set({ answer: e.target.value }); setPreview(null); }}
        multiline
        minRows={3}
        fullWidth
        disabled={disabled}
        autoFocus={autoFocus}
        helperText={channel === 'widget'
          ? 'Write it the way you’d answer a website visitor. Leave out names and details that only apply to one person.'
          : 'Write it the way you’d answer a guest. Leave out names, booking numbers and dates that only apply to one guest.'}
      />

      {suggestExpiry && (
        <Alert
          severity="info"
          icon={<EventOutlinedIcon />}
          sx={{ mt: '8px !important' }}
          action={(
            <Stack direction="row" spacing={0.5}>
              {expiry.date
                ? <Button color="inherit" size="small" onClick={acceptExpiry}>Yes</Button>
                : <Button color="inherit" size="small" onClick={pickDate}>Pick a date</Button>}
              <Button color="inherit" size="small" onClick={dismissExpiry}>No thanks</Button>
            </Stack>
          )}
        >
          {expiry.date
            ? <>This answer mentions “{expiry.phrase}”. Stop using it after {moment(expiry.date).format('MMM D, YYYY')}?</>
            : <>This answer sounds seasonal (“{expiry.phrase}”). Set a date to stop using it?</>}
        </Alert>
      )}

      <ProductChips
        allTours={draft.all_tours}
        tours={draft.tours}
        collections={draft.collections || []}
        accessTags={draft.access_tags || []}
        excludedTours={draft.excluded_tours || []}
        suggested={suggestedTours}
        options={productOptions}
        onChange={(patch) => set(patch)}
        disabled={disabled}
      />

      {canShare && (
        <ChannelChips
          mode="toggle"
          channel={channel}
          channels={draft.channels}
          canShare
          lockCurrent={lockChannel}
          disabled={disabled}
          onChange={(channels) => set({ channels })}
        />
      )}

      <Box>
        <Button
          size="small"
          onClick={() => setShowMore((s) => !s)}
          endIcon={showMore ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ px: 0 }}
        >
          More options
        </Button>
        <Collapse in={showMore}>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Box>
              <TextField
                label="Stop using this answer after"
                type="date"
                size="small"
                inputRef={dateFieldRef}
                value={draft.valid_until || ''}
                onChange={(e) => set({ valid_until: e.target.value || null, valid_until_reason: null })}
                InputLabelProps={{ shrink: true }}
                disabled={disabled}
                sx={{ maxWidth: 260 }}
                helperText={draft.valid_until && draft.valid_until_reason
                  ? `From “${draft.valid_until_reason}” in the answer. We’ll ask you to check it on that date.`
                  : 'Optional. For seasonal answers. We’ll ask you to check it on that date.'}
              />
              {!disabled && (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  {quickStopDates().map((q) => (
                    <Chip
                      key={q.label}
                      size="small"
                      variant={draft.valid_until === q.date ? 'filled' : 'outlined'}
                      color={draft.valid_until === q.date ? 'primary' : 'default'}
                      label={q.label}
                      onClick={() => set({ valid_until: q.date, valid_until_reason: null })}
                    />
                  ))}
                  {draft.valid_until && (
                    <Chip size="small" variant="outlined" label="No end date" onClick={() => set({ valid_until: null, valid_until_reason: null })} />
                  )}
                </Stack>
              )}
            </Box>
          </Stack>
        </Collapse>
      </Box>

      <Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={previewing ? <CircularProgress size={14} /> : <SmartToyOutlinedIcon />}
          onClick={tryIt}
          disabled={previewing || !draft.answer.trim()}
        >
          Try it
        </Button>
        {preview && (
          <Paper
            variant="outlined"
            sx={{ mt: 1.5, p: 1.5, borderRadius: '12px', bgcolor: 'action.hover', maxWidth: 560 }}
          >
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
              {channel === 'widget' ? 'A visitor asks' : 'A guest asks'}: “{question}”
            </Typography>
            {preview.error ? (
              <Typography variant="body2" color="error">Couldn’t get a preview right now.</Typography>
            ) : (
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <SmartToyOutlinedIcon fontSize="small" color="primary" sx={{ mt: '2px' }} />
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{preview.reply}</Typography>
              </Stack>
            )}
            {preview.sample && (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                Sample preview. The live version asks your chatbot with this answer in place.
              </Typography>
            )}
          </Paper>
        )}
      </Box>
    </Stack>
  );
}

// Initial editor state for an item; `answerText` pre-fills the text. `ctx`: the operator's tags
// (useTagScope()), so a card's products start as their tags in the first featured category.
export function draftFor(item, answerText = '', ctx = null) {
  const saved = item.answer;
  if (saved) {
    return {
      answer: answerText || saved.answer,
      all_tours: saved.all_tours,
      tours: saved.tours || [],
      collections: saved.collections || [],
      access_tags: saved.access_tags || [],
      excluded_tours: saved.excluded_tours || [],
      channels: saved.channels?.length ? saved.channels : [item.channel],
      valid_until: saved.valid_until && item.stale_reason === 'expired' ? null : saved.valid_until,
      valid_until_reason: item.stale_reason === 'expired' ? null : (saved.valid_until_reason || null),
    };
  }
  const tours = item.tours || [];
  // Asked about products in a collection -> that collection (it covers every language version).
  const scope = scopeFromTours(tours, ctx);
  // Asked about most of the catalogue (or no product at all) -> default to everything.
  const allTours = tours.length === 0 || scope.tours.length + scope.collections.length >= 4;
  return {
    answer: answerText,
    all_tours: allTours,
    tours: allTours ? [] : scope.tours,
    collections: allTours ? [] : scope.collections,
    access_tags: [],
    excluded_tours: [],
    channels: [item.channel],
    valid_until: null,
    valid_until_reason: null,
  };
}

export function draftIsValid(draft) {
  return !!draft.answer.trim()
    && (draft.all_tours || draft.tours.length > 0 || (draft.collections || []).length > 0 || (draft.access_tags || []).length > 0)
    && draft.channels.length > 0;
}

export function draftToPayload(question, draft) {
  return {
    question: question.trim(),
    answer: draft.answer.trim(),
    all_tours: draft.all_tours,
    tour_ids: draft.all_tours ? [] : draft.tours.map((t) => t.id),
    // System tags of any category (the key predates tag scoping).
    collection_ids: draft.all_tours ? [] : (draft.collections || []).map((c) => c.id),
    atag_ids: draft.all_tours ? [] : (draft.access_tags || []).map((a) => a.id),
    // Left out of the above, "All products" included.
    excluded_tour_ids: (draft.excluded_tours || []).map((t) => t.id),
    channels: draft.channels,
    valid_until: draft.valid_until || null,
    valid_until_reason: draft.valid_until ? (draft.valid_until_reason || null) : null,
  };
}
